import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import DrawerBody from 'components/ui/DrawerBody';
import DrawerHeader from 'components/ui/DrawerHeader';
import DrawerFooter from 'components/ui/DrawerFooter';
import CtaPrimary from 'components/ui/CtaPrimary';
import CtaSecondary from 'components/ui/CtaSecondary';

import errorAnimationData from 'assets/error.json';
import loadingAnimationData from 'assets/loading.json';
import successAnimationData from 'assets/success.json';
import cautionAnimationData from 'assets/caution.json';

import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';
import DrawerSuccessMessages from 'components/ui/DrawerSuccessMessages';
import Lottie from 'react-lottie';
import EventForm from './EventForm/Eventform';

import { editEvent, deleteEvent } from './store/actions';

const EDITING_STATES = {
  form: 'form',
  deleting: 'deleting',
  loading: 'loading',
  success: 'success',
  successDeleting: 'successDeleting',
  error: 'error',
  errorDeleting: 'errorDeleting',
};

const FORM_NAME = 'edit_event_form';

const StyledSecondaryCta = styled(CtaSecondary)`
  background-color: inherit;
  color: ${colors.ULTRAMARINE_BLUE};
  line-height: 36px;
  height: 36px;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  border: none;
  border-radius: 28px !important;
  padding: 1px 24px 0;
  text-align: center;
  cursor: pointer;
  &:hover {
    background-color: inherit;
  }
`;

const EditEvent = ({ eventDetails, handleClose, onSubmissionComplete, editEvent, deleteEvent }) => {
  const [editingState, setEditingState] = useState(EDITING_STATES.form);
  const [tempValues, setTempValues] = useState({});

  const handleEditEvent = useCallback(
    async values => {
      setEditingState(EDITING_STATES.loading);
      try {
        // if the request fails, store the values temporarily to retry
        setTempValues(values);
        await editEvent(values, eventDetails.id);
        setTempValues({});
        setEditingState(EDITING_STATES.success);
        if (onSubmissionComplete) {
          onSubmissionComplete();
        }
      } catch (err) {
        setEditingState(EDITING_STATES.error);
      }
    },
    [editEvent, onSubmissionComplete, eventDetails]
  );

  const handleDeleteEvent = useCallback(
    async () => {
      setEditingState(EDITING_STATES.loading);
      try {
        await deleteEvent('SiteEvent', eventDetails.id);
        setEditingState(EDITING_STATES.successDeleting);
        if (onSubmissionComplete) {
          onSubmissionComplete();
        }
      } catch (err) {
        setEditingState(EDITING_STATES.errorDeleting);
      }
    },
    [deleteEvent, onSubmissionComplete, eventDetails]
  );

  const handleRetry = useCallback(
    () => {
      handleEditEvent(tempValues);
    },
    [handleEditEvent, tempValues]
  );

  const isMobile = window.matchMedia('(max-width: 500px)').matches;
  const lottieSize = isMobile ? 80 : 150;

  if (editingState === EDITING_STATES.form) {
    return (
      <FormFace
        handleClose={handleClose}
        handleEditEvent={handleEditEvent}
        eventDetails={eventDetails}
        setEditingState={setEditingState}
      />
    );
  } else if (editingState === EDITING_STATES.deleting) {
    return (
      <DeletingFace
        lottieSize={lottieSize}
        handleCancel={() => setEditingState(EDITING_STATES.form)}
        handleDeleteEvent={handleDeleteEvent}
      />
    );
  } else if (editingState === EDITING_STATES.loading) {
    return <LoadingFace lottieSize={lottieSize} />;
  } else if (
    editingState === EDITING_STATES.success ||
    editingState === EDITING_STATES.successDeleting
  ) {
    return (
      <SuccessFace
        handleClose={() => {
          setEditingState(EDITING_STATES.form);
          handleClose();
        }}
        lottieSize={lottieSize}
      />
    );
  } else if (
    editingState === EDITING_STATES.error ||
    editingState === EDITING_STATES.errorDeleting
  ) {
    return (
      <ErrorFace
        handleRetry={handleRetry}
        handleClose={() => {
          setEditingState(EDITING_STATES.form);
          handleClose();
        }}
        lottieSize={lottieSize}
        editingState={editingState}
      />
    );
  }
};

const FormFace = ({ handleClose, handleEditEvent, setEditingState, eventDetails }) => {
  return (
    <React.Fragment>
      <DrawerHeader
        onClose={handleClose}
        title={getTranslatedString('landlordPortal.events.eventDetailsHeader')}
        subHeader={getTranslatedString('landlordPortal.events.requiredField')}
      />
      <DrawerBody>
        <EventForm formName={FORM_NAME} formData={eventDetails} handleSubmit={handleEditEvent} />
      </DrawerBody>
      <DrawerFooter
        primaryActionFormName={FORM_NAME}
        primaryActionText={getTranslatedString('save')}
        secondaryActionText={getTranslatedString('landlordPortal.events.cancelEvent')}
        onSecondaryActionClick={() => setEditingState(EDITING_STATES.deleting)}
      />
    </React.Fragment>
  );
};

const SuccessFace = ({ handleClose, lottieSize, editingState }) => {
  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: successAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  let successMessage = getTranslatedString('landlordPortal.events.editSuccess');
  if (editingState === EDITING_STATES.successDeleting) {
    successMessage = getTranslatedString('landlordPortal.events.deleteSuccess');
  }

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={lottieSize} width={lottieSize} />}
      headerText={successMessage}
      bodyText={getTranslatedString('globalDrawer.successBody')}
      cta={<CtaPrimary onClick={handleClose}>{getTranslatedString('close')}</CtaPrimary>}
    />
  );
};

const DeletingFace = ({ lottieSize, handleCancel, handleDeleteEvent }) => {
  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: cautionAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={lottieSize} width={lottieSize} />}
      headerText={getTranslatedString('landlordPortal.events.deleteEventHeader')}
      bodyText={getTranslatedString('landlordPortal.events.deleteEventBody')}
      cta={
        <CtaPrimary onClick={handleDeleteEvent}>
          {getTranslatedString('landlordPortal.events.deleteEventCTA')}
        </CtaPrimary>
      }
      secondaryCta={
        <StyledSecondaryCta onClick={handleCancel}>
          {getTranslatedString('cancel')}
        </StyledSecondaryCta>
      }
    />
  );
};

const ErrorFace = ({ handleRetry, handleClose, lottieSize, editingState }) => {
  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: errorAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  let errorMessage = getTranslatedString('landlordPortal.events.editError');
  if (editingState === EDITING_STATES.errorDeleting) {
    errorMessage = getTranslatedString('landlordPortal.events.deleteError');
  }

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={lottieSize} width={lottieSize} />}
      headerText={errorMessage}
      bodyText={getTranslatedString('globalDrawer.errorBody')}
      cta={
        <CtaPrimary onClick={handleRetry}>{getTranslatedString('globalDrawer.goBack')}</CtaPrimary>
      }
      secondaryCta={
        <StyledSecondaryCta onClick={handleClose}>
          {getTranslatedString('close')}
        </StyledSecondaryCta>
      }
    />
  );
};

const LoadingFace = ({ lottieSize }) => {
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: loadingAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={lottieSize} width={lottieSize} />}
      headerText={getTranslatedString('landlordPortal.events.loadingHeader')}
      bodyText={getTranslatedString('globalDrawer.loadingBody')}
    />
  );
};

const mapStoreToProps = store => ({
  eventDetails: store.landlord.events.site_event,
});

const mapDispatchToProps = {
  editEvent,
  deleteEvent,
};

export default connect(mapStoreToProps, mapDispatchToProps)(EditEvent);
